import { Injectable, InjectionToken, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';

/** Roles a recruiter can hold within a tenant. Kept as a string union so
 *  a future backend contract can extend without a breaking type change. */
export type RecruiterRole = 'Admin' | 'Recruiter' | 'Viewer';

/** Payload the caller supplies to add a new recruiter to the current tenant.
 *  Tenant scope comes from the auth context server-side, so it's not on
 *  the payload — recruiters are always added to the calling user's tenant.
 *  The same shape drives edits (all fields are editable). */
export interface AddRecruiterPayload {
  firstName: string;
  lastName: string;
  email: string;
  role: RecruiterRole;
}

/** A recruiter already on the tenant's roster. The server-assigned `id`
 *  is the stable handle for edit / remove; the remaining fields mirror
 *  AddRecruiterPayload. */
export interface Recruiter extends AddRecruiterPayload {
  id: string;
}

/** Config the consuming app supplies at bootstrap. Same pattern as
 *  ORG_CONTEXT_CONFIG and CV_UPLOAD_CONFIG — keeps the lib app-agnostic. */
export interface TeamServiceConfig {
  /** Absolute base URL of the tenant-management API — e.g.
   *  `https://api.dev.ka-boom.app/api/tenant`. */
  tenantApiUrl: string;
}

export const TEAM_SERVICE_CONFIG = new InjectionToken<TeamServiceConfig>(
  'TEAM_SERVICE_CONFIG',
  {
    factory: () => {
      throw new Error(
        'TEAM_SERVICE_CONFIG is not provided. In your app config, add:\n' +
        "  { provide: TEAM_SERVICE_CONFIG, useValue: { tenantApiUrl: '<url>' } }",
      );
    },
  },
);

/**
 * Team management for the current tenant — the full recruiter lifecycle
 * (list / add / edit / remove).
 *
 * Backend note: the tenant-management recruiter endpoints aren't yet
 * implemented on kaboom-tenant-management, so every method below is
 * stubbed against an in-memory roster (seeded so the drawer's list,
 * add, edit, and remove flows are all exercisable end-to-end without
 * the backend). Each method mutates or reads `this.roster` and returns
 * a completed observable; when the endpoints land, swap each body for
 * the `this.http.*` call noted in its TODO — the callable shapes and
 * return types stay the same, so the drawer needs no changes.
 *
 * Diagnostics: the stubs log only non-PII context (role / id / target
 * URL) — never names or emails — so nothing sensitive reaches the
 * browser console even in the interim.
 */
@Injectable({ providedIn: 'root' })
export class TeamService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(TEAM_SERVICE_CONFIG);

  /** In-memory stand-in for the tenant's recruiter roster. Seeded with a
   *  few members so the management UI has something to render, edit, and
   *  remove before the backend exists. */
  private roster: Recruiter[] = [
    { id: 'seed-jane',  firstName: 'Jane',  lastName: 'Murphy', email: 'jane.murphy@example.com', role: 'Admin' },
    { id: 'seed-tom',   firstName: 'Tom',   lastName: 'Ray',    email: 'tom.ray@example.com',      role: 'Recruiter' },
    { id: 'seed-priya', firstName: 'Priya', lastName: 'Nair',   email: 'priya.nair@example.com',   role: 'Viewer' },
  ];

  /** List the current tenant's recruiters. Returns copies so callers
   *  can't mutate the stub roster by reference. */
  listRecruiters(): Observable<Recruiter[]> {
    // TODO(follow-up): return this.http.get<Recruiter[]>(
    //   `${this.config.tenantApiUrl}/recruiters`);
    return of(this.roster.map(r => ({ ...r })));
  }

  /** Add (invite) a recruiter. Resolves to the created record, including
   *  the server-assigned id the caller needs for later edit / remove. */
  addRecruiter(payload: AddRecruiterPayload): Observable<Recruiter> {
    // TODO(follow-up): return this.http.post<Recruiter>(
    //   `${this.config.tenantApiUrl}/recruiters`, payload);
    const created: Recruiter = { id: crypto.randomUUID(), ...payload };
    this.roster = [...this.roster, created];
    console.info('[TeamService] addRecruiter (stub)', { role: payload.role, tenantApiUrl: this.config.tenantApiUrl });
    return of({ ...created });
  }

  /** Update an existing recruiter. Resolves to the updated record, or
   *  errors if the id isn't on the roster — mirroring the 404 the real
   *  `PUT /recruiters/{id}` would return, rather than a false success. */
  updateRecruiter(id: string, payload: AddRecruiterPayload): Observable<Recruiter> {
    // TODO(follow-up): return this.http.put<Recruiter>(
    //   `${this.config.tenantApiUrl}/recruiters/${id}`, payload);
    if (!this.roster.some(r => r.id === id)) {
      return throwError(() => new Error('Recruiter not found'));
    }
    const updated: Recruiter = { id, ...payload };
    this.roster = this.roster.map(r => (r.id === id ? updated : r));
    console.info('[TeamService] updateRecruiter (stub)', { id, role: payload.role, tenantApiUrl: this.config.tenantApiUrl });
    return of({ ...updated });
  }

  /** Remove a recruiter from the tenant. Idempotent — resolves whether or
   *  not the id was present, mirroring the intended 204-always contract. */
  removeRecruiter(id: string): Observable<void> {
    // TODO(follow-up): return this.http.delete<void>(
    //   `${this.config.tenantApiUrl}/recruiters/${id}`);
    this.roster = this.roster.filter(r => r.id !== id);
    console.info('[TeamService] removeRecruiter (stub)', { id, tenantApiUrl: this.config.tenantApiUrl });
    return of(undefined);
  }
}
